import React, { useCallback, useState } from 'react';
import { StyleSheet, View, Platform, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout } from '@/components';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useResidentList } from '@/hooks/admin/useResidentList';
import { ResidentDataTable } from '@/components';
import { IResidentTableRow } from '@/types/admin/residentList';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { PageHeader } from '@/components/shared/redesign';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { toResidentCsvRows, RESIDENT_CSV_COLUMNS } from '@/utils/admin/residentsList/csv';
import { ResidentDetailsSidePanel } from '@/components/admin/resident/desktop/ResidentDetailsSidePanel';
import { ResidentListSkeleton } from './ResidentListSkeleton';
import { DesktopResidentListSkeleton } from './DesktopResidentListSkeleton';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { DEFAULT_RESIDENT_FILTER_MODEL, DEFAULT_RESIDENT_SORT_MODEL, DEFAULT_PAGINATION_MODEL } from '@/constants/admin/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';


type ResidentListProps = StackScreenProps<AdminStackParamList, 'ResidentList'>;

const CSV_COLUMNS = RESIDENT_CSV_COLUMNS;

export const ResidentList: React.FC<ResidentListProps> = ({
	navigation,
}) => {
	const [csvData, setCsvData] = useState<(string | number | boolean | null)[][]>([CSV_COLUMNS]);
	const { showApiError } = useErrorToast();
	const { layoutMode } = useResponsive();

	// Desktop side panel state
	const [detailsPanelVisible, setDetailsPanelVisible] = useState(false);
	const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
	const [selectedLeaseId, setSelectedLeaseId] = useState<number | null>(null);

	const {
		data,
		tableRows,
		isLoading,
		enhancedError,
		retryCount,
		canRetry,
		manualRetry,
		paginationModel,
		filterModel,
		searchQuery,
	} = useResidentList();

	// Show toast for API errors (guard duplicates)
	useErrorToastOnce(enhancedError, showApiError);
	// Keep UI immediate; debounce handled inside hook effects

	// Format CSV data using shared util
	const formatCsvData = useCallback((newData: IResidentTableRow[]) => {
		if (!newData || newData.length === 0) {
			setCsvData([CSV_COLUMNS]);
			return;
		}
		setCsvData(toResidentCsvRows(newData));
	}, []);

	// Handle resident selection
	const selectResidentDetails = useCallback((resident: IResidentTableRow) => {
		if (layoutMode === 'desktop') {
			setSelectedAccountId(resident.accountId);
			setSelectedLeaseId(resident.leaseId);
			setDetailsPanelVisible(true);
			return;
		}
		navigation.navigate('ResidentDetails', {
			accountId: resident.accountId,
			leaseId: resident.leaseId,
		});
	}, [navigation, layoutMode]);

	// Handle download functionality
	const handleDownload = useCallback(() => {
		if (Platform.OS === 'web') {
			const filename = `resio-residents-${new Date().toJSON().slice(0, 10)}`;
			try {
				downloadCsvFrom2DArray(toResidentCsvRows(tableRows), filename);
			} catch (e) {
				console.error('Failed to download CSV', e);
			}
		} else {
			// Non-web platforms: could share file or show a toast; for now, no-op
			console.log('CSV export is available on web only.');
		}
	}, [tableRows]);

	// Determine if we should show data or empty state
	// Show data if we have data, regardless of loading state (prevents blinking)
	const hasData = data.count > 0;

	// Show error state if we have a critical error and no data and no search/filter applied
	const hasSearchOrFilter = searchQuery.trim() !== '' || filterModel.items.length > 0;
	const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchOrFilter;

	// Show loading state if we're loading and have no data yet and no search/filter applied
	const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchOrFilter;

	// Always show the data table if there's a search/filter applied, even with no results
	const showDataTable = hasData || hasSearchOrFilter;

	// Render error state component
	const renderErrorState = () => (
		<AdminErrorState
			title="Unable to Load Residents"
			message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the resident list.'}
			retryCount={retryCount}
			canRetry={canRetry}
			onRetry={manualRetry}
		/>
	);

	const headerSubtitle = data.count > 0 ? `${data.count} residents` : '';

	// Unified DataTableProvider for both desktop and mobile layouts
	return (
		<DataTableProvider
			tableKey="admin:residents"
			defaults={{
				filterModel: DEFAULT_RESIDENT_FILTER_MODEL,
				sortModel: DEFAULT_RESIDENT_SORT_MODEL as any,
				paginationModel: paginationModel || DEFAULT_PAGINATION_MODEL,
			}}
		>
			{layoutMode === 'desktop' ? (
				<AdminErrorBoundary context="ResidentList">
					<DesktopAdminLayout>
						<PageHeader
							title="Residents"
							subtitle={headerSubtitle}
							rightAction={<ExportTableButton onPress={handleDownload} />}
						/>
						{showLoadingState ? (
							<DesktopResidentListSkeleton />
						) : showErrorState ? (
							renderErrorState()
						) : (
							<View style={styles.desktopDataContainer}>
								<ResidentDataTable
									data={data}
									rows={tableRows}
									isLoading={isLoading}
									onResidentSelect={selectResidentDetails}
									updateCsvData={formatCsvData}
									showSearch={false}
									showSort={true}
								/>
								<ResidentDetailsSidePanel
									visible={detailsPanelVisible}
									onClose={() => setDetailsPanelVisible(false)}
									accountId={selectedAccountId}
									leaseId={selectedLeaseId}
								/>
							</View>
						)}
					</DesktopAdminLayout>
				</AdminErrorBoundary>
			) : (
				<AdminErrorBoundary context="ResidentList">
					<BaseAdminScreen
						screenName="ResidentList"
						navigation={navigation}
						showPlaceholder={false}
						scrollEnabled={false}
						contentContainerStyle={styles.baseScreenContent}
					>
						<View style={styles.mobileHeaderArea}>
							<PageHeader title="Residents" subtitle={headerSubtitle} />
						</View>
						<AdminEmptyScreenLayout
							title="Residents"
							emptyStateType="residents"
							showAddButton={false}
							showTitleBar={false}
							hasContent={showDataTable || showErrorState || showLoadingState}
							disableScrollView={showDataTable}
						>
							{showLoadingState ? (
								<ResidentListSkeleton />
							) : showErrorState ? (
								renderErrorState()
							) : showDataTable ? (
								<View style={styles.dataContainer}>
									<ResidentDataTable
										data={data}
										rows={tableRows}
										isLoading={isLoading}
										onResidentSelect={selectResidentDetails}
										updateCsvData={formatCsvData}
										showSearch={false}
									/>
								</View>
							) : null}
						</AdminEmptyScreenLayout>
					</BaseAdminScreen>
				</AdminErrorBoundary>
			)}
		</DataTableProvider>
	);
};

/**
 * Compact "Export Table" button used as the `rightAction` slot of the shared
 * `PageHeader` on desktop. Mirrors the secondary-button styling that the old
 * `DesktopPageHeader` produced internally so the visual treatment stays
 * unchanged across the migration.
 */
const ExportTableButton: React.FC<{ onPress: () => void }> = ({ onPress }) => {
	const styles = useExportButtonStyles();
	const { admin } = useAdminColors();
	return (
		<TouchableOpacity style={styles.button} onPress={onPress} activeOpacity={0.8}>
			<SVG_COMPONENTS.DOWNLOAD width={16} height={16} color={admin.dataTable.text} />
			<Text style={styles.label}>Export Table</Text>
		</TouchableOpacity>
	);
};

const useExportButtonStyles = () =>
	useThemedStyles(({ components }) => {
		const admin = components.admin;
		return StyleSheet.create({
			button: {
				flexDirection: 'row',
				alignItems: 'center',
				paddingHorizontal: 16,
				paddingVertical: 10,
				borderRadius: 8,
				gap: 8,
				backgroundColor: 'transparent',
				borderWidth: 1,
				borderColor: admin.dataTable.border,
			},
			label: {
				color: admin.dataTable.text,
				fontSize: 14,
				fontWeight: '600',
			},
		});
	});

const styles = StyleSheet.create({
	baseScreenContent: {
		paddingHorizontal: 0,
		paddingTop: 0,
		paddingBottom: 0,
		flexGrow: 1,
	},
	dataContainer: {
		flex: 1,
		width: '100%',
	},
	desktopDataContainer: {
		flex: 1,
		width: '100%',
		backgroundColor: 'transparent',
	},
	mobileHeaderArea: {
		paddingHorizontal: 16,
		gap: 12,
	},
});
